import { apiClient } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import {
  API_EDI_MY_BUYERS,
  API_EDI_MY_SUPPLIERS,
  API_EDI_ACTIVE_PARTY_DEFAULTS,
} from '../../constants/apiConstants';

// ============================================================================
// Types — mirror the backend payloads (active-party-top-bar spec).
// ============================================================================

/** A buyer or supplier party, resolved id -> code/name/type. */
export interface MyPartyItem {
  partyId: number;
  partyCode: string;
  partyName: string;
  partyType?: string | null;
}

/**
 * Sentinel party id representing an explicit "All Buyers" / "All Suppliers"
 * selection (spec: edi-admin-and-scoped-party-selector). Distinct from `null`
 * (= unresolved) — an empty axis is NEVER "all". At query time an "All" axis
 * sends no party id (the authorized axis / global EDI access bounds the result).
 */
export const ALL_PARTY_ID = -1;

/** Build the "All" sentinel item for an axis. `partyName` is a display fallback;
 *  the selector renders the localized "All Buyers/All Suppliers" label itself. */
export const makeAllParty = (): MyPartyItem => ({
  partyId: ALL_PARTY_ID,
  partyCode: '*',
  partyName: 'All',
});

/** True when a party value is the explicit "All" sentinel. */
export const isAllParty = (p: MyPartyItem | null | undefined): boolean =>
  p != null && p.partyId === ALL_PARTY_ID;

/** The user's EDI scope direction (from `my-buyers`). */
export type EdiScopeDirection = 'unrestricted' | 'buyer' | 'supplier' | 'hybrid';

/** Response of GET .../user-party-assignments/my-buyers. */
export interface MyBuyersResponse {
  isUnrestricted: boolean;
  affiliationType: string;
  buyers: MyPartyItem[];
  totalCount: number;
  tenantDefaultBuyerPartyId: number | null;
  tenantDefaultSupplierPartyId: number | null;

  // ── My EDI scope (spec: edi-admin-and-scoped-party-selector) ──
  /** unrestricted | buyer | supplier | hybrid. */
  direction: EdiScopeDirection;
  /** True when the user has GLOBAL EDI read (edi.admin / tenant / sys admin) →
   *  "All" means tenant-wide and is offered on BOTH axes. */
  hasGlobalEdiRead: boolean;
  /** The user's OWN assigned buyer parties (owned buyer axis). */
  myBuyerParties: MyPartyItem[];
  /** The user's OWN assigned supplier parties (owned supplier axis). */
  mySupplierParties: MyPartyItem[];
}

/** The per-tenant default active buyer/supplier (EDI Admin tab). */
export interface ActivePartyDefaults {
  defaultBuyerPartyId: number | null;
  defaultSupplierPartyId: number | null;
}

interface ResultEnvelope<T> {
  succeeded: boolean;
  statusCode?: number;
  messages?: string[];
  data?: T;
}

const EMPTY_BUYERS: MyBuyersResponse = {
  isUnrestricted: false,
  affiliationType: '',
  buyers: [],
  totalCount: 0,
  tenantDefaultBuyerPartyId: null,
  tenantDefaultSupplierPartyId: null,
  direction: 'unrestricted',
  hasGlobalEdiRead: false,
  myBuyerParties: [],
  mySupplierParties: [],
};

export const activePartyService = {
  /** The signed-in user's selectable buyer list (paged + searchable for internal users). */
  async getMyBuyers(
    tenantId: number,
    opts?: { searchText?: string; pageIndex?: number; pageSize?: number }
  ): Promise<MyBuyersResponse> {
    try {
      const response = await apiClient.get<ResultEnvelope<MyBuyersResponse>>(API_EDI_MY_BUYERS, {
        params: {
          tenantId,
          searchText: opts?.searchText || undefined,
          pageIndex: opts?.pageIndex,
          pageSize: opts?.pageSize,
        },
      });
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Failed to load buyers');
      }
      return response.data.data ?? EMPTY_BUYERS;
    } catch (error) {
      logger.error('Failed to load my-buyers', error);
      throw error;
    }
  },

  /** The suppliers available to the signed-in user for a chosen buyer. */
  async getMySuppliers(tenantId: number, buyerPartyId: number): Promise<MyPartyItem[]> {
    try {
      const response = await apiClient.get<ResultEnvelope<{ suppliers: MyPartyItem[] }>>(
        API_EDI_MY_SUPPLIERS,
        { params: { tenantId, buyerPartyId } }
      );
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Failed to load suppliers');
      }
      return response.data.data?.suppliers ?? [];
    } catch (error) {
      logger.error('Failed to load my-suppliers', error);
      throw error;
    }
  },

  /** Reads the tenant's current default active buyer/supplier (EDI Admin tab). */
  async getActivePartyDefaults(tenantId: number): Promise<ActivePartyDefaults> {
    const response = await apiClient.get<ResultEnvelope<ActivePartyDefaults>>(
      API_EDI_ACTIVE_PARTY_DEFAULTS,
      { params: { tenantId } }
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.join(', ') || 'Failed to load active-party defaults');
    }
    return response.data.data ?? { defaultBuyerPartyId: null, defaultSupplierPartyId: null };
  },

  /** Writes the tenant's default active buyer/supplier (EDI Admin tab). */
  async updateActivePartyDefaults(
    tenantId: number,
    payload: ActivePartyDefaults
  ): Promise<ActivePartyDefaults> {
    const response = await apiClient.put<ResultEnvelope<ActivePartyDefaults>>(
      `${API_EDI_ACTIVE_PARTY_DEFAULTS}?tenantId=${tenantId}`,
      payload
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.join(', ') || 'Failed to update active-party defaults');
    }
    return response.data.data ?? payload;
  },
};
